/**
 * Selector de proyecto del formulario de tarea: «Sin proyecto», los proyectos activos y «＋ Agregar proyecto…».
 *
 * La última opción (solo si se puede escribir) abre el bloque «Nuevo proyecto» para dar de alta un proyecto
 * sin salir del formulario, con un enlace al repo opcional. Si ya existía (409 `proyecto_duplicado`) se
 * selecciona el existente, sin error. Un proyecto inactivo solo aparece si es el que la tarea ya tiene (así
 * editar no lo pierde).
 *
 * Devuelve dos hermanos (el campo y el bloque) y no un envoltorio: en una rejilla el bloque ocupa la fila
 * completa (`grid-column: 1 / -1`) en vez de apretarse dentro de la columna «Proyecto».
 */
import { useEffect, useRef, useState } from 'react';
import { enlaceRepoSchema, nombreProyectoSchema } from '@cdtec-backlog/contrato';
import { ErrorDeApi, mensajeDeError } from '../../lib/api';
import { proyectoExistente } from './api';
import { useCrearProyecto, useProyectos } from './hooks';
import './SelectorProyecto.css';

const AGREGAR = '__agregar__';

interface Props {
  /** Id del proyecto en texto, o vacío para «Sin proyecto». */
  valor: string;
  onCambio: (valor: string) => void;
  /** Muestra la opción «＋ Agregar proyecto…» (la API es quien exige el permiso real). */
  puedeAgregar: boolean;
  /** La etiqueta «Proyecto» queda solo para lectores (en la ficha el título de la propiedad ya la muestra). */
  etiquetaOculta?: boolean;
  /**
   * Se llama tras crear (o encontrar activo) el proyecto, después de `onCambio`. La ficha lo usa para guardar
   * la tarea en el mismo paso. Si el existente está inactivo no se llama: queda elegido y se ve el aviso.
   */
  onAgregado?: (valor: string) => void;
  /** Avisa cuando se abre o cierra el bloque «Nuevo proyecto» (la ficha oculta sus Guardar/Cancelar). */
  onBloqueNuevo?: (abierto: boolean) => void;
}

/** Qué campo del bloque tiene el error, para mostrarlo justo debajo de él. */
type FallaEn = { campo: 'nombre' | 'enlace'; mensaje: string };

export function SelectorProyecto({
  valor,
  onCambio,
  puedeAgregar,
  etiquetaOculta = false,
  onAgregado,
  onBloqueNuevo,
}: Props) {
  const proyectos = useProyectos();
  const crear = useCrearProyecto();
  const [agregando, setAgregando] = useState(false);
  const [nombre, setNombre] = useState('');
  const [enlace, setEnlace] = useState('');
  const [error, setError] = useState<FallaEn | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  const campoEnlace = useRef<HTMLInputElement>(null);
  const select = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (agregando) campo.current?.focus();
  }, [agregando]);

  // En un efecto y no en `cerrar`: así también avisa cuando se abre desde el select.
  useEffect(() => {
    onBloqueNuevo?.(agregando);
  }, [agregando, onBloqueNuevo]);

  const lista = (proyectos.data ?? []).filter((p) => p.activo || String(p.id) === valor);

  const cerrar = () => {
    setAgregando(false);
    setNombre('');
    setEnlace('');
    setError(null);
    // El foco vuelve al selector para no perder el lugar con el teclado.
    setTimeout(() => select.current?.focus(), 0);
  };

  const agregar = async () => {
    const analisisNombre = nombreProyectoSchema.safeParse(nombre);
    if (!analisisNombre.success) {
      setError({
        campo: 'nombre',
        mensaje: analisisNombre.error.issues[0]?.message ?? 'Nombre inválido.',
      });
      campo.current?.focus();
      return;
    }
    const analisisEnlace = enlaceRepoSchema.safeParse(enlace);
    if (!analisisEnlace.success) {
      setError({
        campo: 'enlace',
        mensaje: analisisEnlace.error.issues[0]?.message ?? 'Enlace inválido.',
      });
      campoEnlace.current?.focus();
      return;
    }
    setError(null);
    try {
      const proyecto = await crear.mutateAsync({
        nombre: analisisNombre.data,
        ...(analisisEnlace.data ? { enlaceRepo: analisisEnlace.data } : {}),
      });
      onCambio(String(proyecto.id));
      setAviso(null);
      cerrar();
      onAgregado?.(String(proyecto.id));
    } catch (falla) {
      const existente = proyectoExistente(falla);
      if (existente) {
        onCambio(String(existente.id));
        setAviso(
          existente.activo
            ? null
            : `«${existente.nombre}» ya existía pero está inactivo. Pide a un administrador que lo active.`,
        );
        cerrar();
        if (existente.activo) onAgregado?.(String(existente.id));
        return;
      }
      setError({
        campo: 'nombre',
        mensaje:
          falla instanceof ErrorDeApi ? mensajeDeError(falla) : 'No se pudo agregar el proyecto.',
      });
      campo.current?.focus();
    }
  };

  // Enter agrega el proyecto (no debe enviar el formulario de la tarea); Escape cierra solo este bloque.
  const teclas = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      void agregar();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // React: que el formulario de la ficha (que cancela con Esc) no lo reciba. Nativo: el panel tampoco.
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      cerrar();
    }
  };

  const errorNombre = error?.campo === 'nombre' ? error.mensaje : null;
  const errorEnlace = error?.campo === 'enlace' ? error.mensaje : null;

  return (
    <>
      <div className="campo">
        <label htmlFor="tarea-proyecto" className={etiquetaOculta ? 'solo-lectores' : undefined}>
          Proyecto
        </label>
        <select
          id="tarea-proyecto"
          ref={select}
          value={agregando ? AGREGAR : valor}
          onChange={(e) => {
            if (e.target.value === AGREGAR) {
              setAgregando(true);
              return;
            }
            if (agregando) cerrar();
            setAviso(null);
            onCambio(e.target.value);
          }}
        >
          <option value="">Sin proyecto</option>
          {lista.map((proyecto) => (
            <option key={proyecto.id} value={String(proyecto.id)}>
              {proyecto.activo ? proyecto.nombre : `${proyecto.nombre} (inactivo)`}
            </option>
          ))}
          {puedeAgregar && <option value={AGREGAR}>＋ Agregar proyecto…</option>}
        </select>
        {aviso && (
          <p role="status" className="selector-proyecto__aviso">
            {aviso}
          </p>
        )}
      </div>
      {agregando && (
        <div
          className="selector-proyecto__nuevo"
          role="group"
          aria-labelledby="proyecto-nuevo-titulo"
          onKeyDown={teclas}
        >
          <p id="proyecto-nuevo-titulo" className="selector-proyecto__titulo">
            Nuevo proyecto
          </p>
          <div className="selector-proyecto__campos">
            <div className="campo">
              <label htmlFor="proyecto-nuevo">Nombre</label>
              <input
                id="proyecto-nuevo"
                ref={campo}
                type="text"
                value={nombre}
                maxLength={100}
                placeholder="cdtec-backlog o Infraestructura GCP"
                autoComplete="off"
                aria-invalid={errorNombre ? true : undefined}
                aria-describedby={errorNombre ? 'proyecto-nuevo-error' : undefined}
                onChange={(e) => setNombre(e.target.value)}
              />
              {errorNombre && (
                <p id="proyecto-nuevo-error" role="alert" className="campo__error">
                  {errorNombre}
                </p>
              )}
            </div>
            <div className="campo">
              <label htmlFor="proyecto-nuevo-enlace">Enlace al repo (opcional)</label>
              <input
                id="proyecto-nuevo-enlace"
                ref={campoEnlace}
                type="text"
                inputMode="url"
                value={enlace}
                maxLength={500}
                placeholder="https://…"
                autoComplete="off"
                aria-invalid={errorEnlace ? true : undefined}
                aria-describedby={errorEnlace ? 'proyecto-nuevo-enlace-error' : undefined}
                onChange={(e) => setEnlace(e.target.value)}
              />
              {errorEnlace && (
                <p id="proyecto-nuevo-enlace-error" role="alert" className="campo__error">
                  {errorEnlace}
                </p>
              )}
            </div>
          </div>
          <div className="selector-proyecto__acciones">
            <button type="button" className="boton" onClick={cerrar}>
              Cancelar
            </button>
            <button
              type="button"
              className="boton boton--primario"
              disabled={crear.isPending}
              onClick={() => void agregar()}
            >
              Agregar
            </button>
          </div>
        </div>
      )}
    </>
  );
}
